import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Check, Loader } from 'lucide-react';
const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    register
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Registration form, 2: OTP verification
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    nationality: 'Singapore',
    preferredLanguage: 'EN',
    marketingConsent: false,
    termsAccepted: false
  });
  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    termsAccepted: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when field is edited
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      ...formErrors
    };
    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      valid = false;
    }
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }
    // Phone validation (Singapore format)
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
      valid = false;
    } else if (!/^(\+65|65)?[89]\d{7}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid Singapore phone number';
      valid = false;
    }
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      valid = false;
    }
    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      valid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }
    // Date of Birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
      valid = false;
    }
    // Terms acceptance validation
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms and conditions';
      valid = false;
    }
    setFormErrors(newErrors);
    return valid;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      await register(formData);
      // In a real app, the API would send an OTP to the user's email
      setStep(2);
      startResendTimer();
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    if (!otpCode.trim()) {
      setOtpError('Please enter the verification code');
      return;
    }
    setIsSubmitting(true);
    try {
      // In a real app, this would verify the OTP with the backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      // For demo, consider any 6-digit code as valid
      if (!/^\d{6}$/.test(otpCode)) {
        throw new Error('Invalid verification code format');
      }
      // Navigate to success page or login
      navigate('/auth/register-success');
    } catch (error) {
      if (error instanceof Error) {
        setOtpError(error.message);
      } else {
        setOtpError('Failed to verify code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleResendOTP = async () => {
    if (resendDisabled) return;
    setIsSubmitting(true);
    try {
      // In a real app, this would resend the OTP
      await new Promise(resolve => setTimeout(resolve, 1000));
      startResendTimer();
    } catch (error) {
      setOtpError('Failed to resend verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const startResendTimer = () => {
    setResendDisabled(true);
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  return <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 max-w-3xl mx-auto" data-id="element-131">
      {step === 1 ? <>
          <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-132">
            Create Your Account
          </h2>
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-133">
              <AlertCircle size={20} className="mr-2" data-id="element-134" />
              <span data-id="element-135">{error}</span>
            </div>}
          <form onSubmit={handleSubmit} data-id="element-136">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-137">
              {/* Full Name */}
              <div className="md:col-span-2" data-id="element-138">
                <label htmlFor="fullName" className="block text-navy-900 font-medium mb-2" data-id="element-139">
                  Full Name *
                </label>
                <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${formErrors.fullName ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter your full name" data-id="element-140" />
                {formErrors.fullName && <p className="text-red-500 text-sm mt-1" data-id="element-141">
                    {formErrors.fullName}
                  </p>}
              </div>
              {/* Email */}
              <div data-id="element-142">
                <label htmlFor="email" className="block text-navy-900 font-medium mb-2" data-id="element-143">
                  Email Address *
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`} placeholder="your@email.com" data-id="element-144" />
                {formErrors.email && <p className="text-red-500 text-sm mt-1" data-id="element-145">
                    {formErrors.email}
                  </p>}
              </div>
              {/* Phone */}
              <div data-id="element-146">
                <label htmlFor="phone" className="block text-navy-900 font-medium mb-2" data-id="element-147">
                  Phone Number *
                </label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`} placeholder="+65 9123 4567" data-id="element-148" />
                {formErrors.phone && <p className="text-red-500 text-sm mt-1" data-id="element-149">
                    {formErrors.phone}
                  </p>}
              </div>
              {/* Password */}
              <div data-id="element-150">
                <label htmlFor="password" className="block text-navy-900 font-medium mb-2" data-id="element-151">
                  Password *
                </label>
                <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${formErrors.password ? 'border-red-500' : 'border-gray-300'}`} placeholder="Minimum 8 characters" data-id="element-152" />
                {formErrors.password && <p className="text-red-500 text-sm mt-1" data-id="element-153">
                    {formErrors.password}
                  </p>}
              </div>
              {/* Confirm Password */}
              <div data-id="element-154">
                <label htmlFor="confirmPassword" className="block text-navy-900 font-medium mb-2" data-id="element-155">
                  Confirm Password *
                </label>
                <input type="password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`} placeholder="Re-enter your password" data-id="element-156" />
                {formErrors.confirmPassword && <p className="text-red-500 text-sm mt-1" data-id="element-157">
                    {formErrors.confirmPassword}
                  </p>}
              </div>
              {/* Date of Birth */}
              <div data-id="element-158">
                <label htmlFor="dateOfBirth" className="block text-navy-900 font-medium mb-2" data-id="element-159">
                  Date of Birth *
                </label>
                <input type="date" id="dateOfBirth" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${formErrors.dateOfBirth ? 'border-red-500' : 'border-gray-300'}`} data-id="element-160" />
                {formErrors.dateOfBirth && <p className="text-red-500 text-sm mt-1" data-id="element-161">
                    {formErrors.dateOfBirth}
                  </p>}
              </div>
              {/* Nationality */}
              <div data-id="element-162">
                <label htmlFor="nationality" className="block text-navy-900 font-medium mb-2" data-id="element-163">
                  Nationality *
                </label>
                <select id="nationality" name="nationality" value={formData.nationality} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-164">
                  <option value="Singapore" data-id="element-165">Singapore</option>
                  <option value="Malaysia" data-id="element-166">Malaysia</option>
                  <option value="Indonesia" data-id="element-167">Indonesia</option>
                  <option value="Brunei" data-id="element-168">Brunei</option>
                  <option value="Other" data-id="element-169">Other</option>
                </select>
              </div>
              {/* Preferred Language */}
              <div data-id="element-170">
                <label htmlFor="preferredLanguage" className="block text-navy-900 font-medium mb-2" data-id="element-171">
                  Preferred Language
                </label>
                <div className="flex space-x-4" data-id="element-172">
                  <label className="flex items-center" data-id="element-173">
                    <input type="radio" name="preferredLanguage" value="EN" checked={formData.preferredLanguage === 'EN'} onChange={handleChange} className="mr-2" data-id="element-174" />
                    <span data-id="element-175">English</span>
                  </label>
                  <label className="flex items-center" data-id="element-176">
                    <input type="radio" name="preferredLanguage" value="MY" checked={formData.preferredLanguage === 'MY'} onChange={handleChange} className="mr-2" data-id="element-177" />
                    <span data-id="element-178">Malay</span>
                  </label>
                </div>
              </div>
              {/* Marketing Consent */}
              <div className="md:col-span-2" data-id="element-179">
                <label className="flex items-start" data-id="element-180">
                  <input type="checkbox" name="marketingConsent" checked={formData.marketingConsent} onChange={handleChange} className="mt-1 mr-2" data-id="element-181" />
                  <span className="text-navy-700 text-sm" data-id="element-182">
                    I agree to receive marketing communications about offers,
                    promotions, and updates from Kembara Ustaz.
                  </span>
                </label>
              </div>
              {/* Terms & Conditions */}
              <div className="md:col-span-2" data-id="element-183">
                <label className="flex items-start" data-id="element-184">
                  <input type="checkbox" name="termsAccepted" checked={formData.termsAccepted} onChange={handleChange} className={`mt-1 mr-2 ${formErrors.termsAccepted ? 'border-red-500' : ''}`} data-id="element-185" />
                  <span className="text-navy-700 text-sm" data-id="element-186">
                    I agree to the{' '}
                    <Link to="/terms" className="text-gold-500 hover:underline" data-id="element-187">
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-gold-500 hover:underline" data-id="element-188">
                      Privacy Policy
                    </Link>{' '}
                    of Kembara Ustaz. *
                  </span>
                </label>
                {formErrors.termsAccepted && <p className="text-red-500 text-sm mt-1 ml-6" data-id="element-189">
                    {formErrors.termsAccepted}
                  </p>}
              </div>
            </div>
            <div className="mt-8" data-id="element-190">
              <button type="submit" disabled={isSubmitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center" data-id="element-191">
                {isSubmitting ? <>
                    <Loader size={20} className="animate-spin mr-2" data-id="element-192" />
                    Creating Account...
                  </> : 'Create Account'}
              </button>
            </div>
          </form>
          <div className="mt-6 text-center" data-id="element-193">
            <p className="text-navy-700" data-id="element-194">
              Already have an account?{' '}
              <Link to="/auth/login" className="text-gold-500 hover:underline font-medium" data-id="element-195">
                Log In
              </Link>
            </p>
          </div>
        </> : <>
          <div className="text-center mb-8" data-id="element-196">
            <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-197">
              <Check size={32} className="text-navy-900" data-id="element-198" />
            </div>
            <h2 className="text-2xl font-bold text-navy-900" data-id="element-199">
              Verify Your Email
            </h2>
            <p className="text-navy-700 mt-2" data-id="element-200">
              We've sent a verification code to{' '}
              <span className="font-medium" data-id="element-201">{formData.email}</span>
            </p>
          </div>
          {otpError && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-202">
              <AlertCircle size={20} className="mr-2" data-id="element-203" />
              <span data-id="element-204">{otpError}</span>
            </div>}
          <form onSubmit={handleVerifyOTP} data-id="element-205">
            <div className="mb-6" data-id="element-206">
              <label htmlFor="otpCode" className="block text-navy-900 font-medium mb-2" data-id="element-207">
                Verification Code
              </label>
              <input type="text" id="otpCode" value={otpCode} onChange={e => setOtpCode(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 text-center text-lg tracking-widest" placeholder="123456" maxLength={6} data-id="element-208" />
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center" data-id="element-209">
              {isSubmitting ? <>
                  <Loader size={20} className="animate-spin mr-2" data-id="element-210" />
                  Verifying...
                </> : 'Verify Email'}
            </button>
          </form>
          <div className="mt-6 text-center" data-id="element-211">
            <p className="text-navy-700" data-id="element-212">
              Didn't receive the code?{' '}
              <button onClick={handleResendOTP} disabled={resendDisabled} className={`text-gold-500 hover:underline font-medium ${resendDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-213">
                {resendDisabled ? `Resend in ${countdown}s` : 'Resend Code'}
              </button>
            </p>
          </div>
        </>}
    </div>;
};
export default RegisterForm;